import type { ValidComponent } from "solid-js";
import { splitProps } from "solid-js";

import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import * as TabsPrimitive from "@kobalte/core/tabs";

import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "~/util/utils";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("flex gap-x-1 h-10 items-center justify-center rounded-md", {
	variants: {},
});

type TabsListProps<T extends ValidComponent = "div"> = TabsPrimitive.TabsListProps<T> & {
	class?: string | undefined;
};

const TabsList = <T extends ValidComponent = "div">(props: PolymorphicProps<T, TabsListProps<T>>) => {
	const [local, others] = splitProps(props as TabsListProps, ["class"]);
	return (
		<TabsPrimitive.List
			class={cn(tabsListVariants(), local.class)}
			{...others}
		/>
	);
};

const tabsTriggerVariants = cva(
	cn(
		"items-center justify-center whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium ring-offset-background transition-all",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
		"disabled:pointer-events-none disabled:opacity-50",
	),
	{
		variants: {
			variant: {
				default: "bg-black/5 text-black data-[selected]:bg-black/15",
				filled: "bg-accent/[7%] text-accent fill-accent data-[selected]:bg-accent data-[selected]:text-white data-[selected]:fill-white",
			},
			radius: {
				default: "rounded-md",
				sm: "rounded-sm",
				md: "rounded-md",
				lg: "rounded-lg",
				xl: "rounded-xl",
			},
		},
		defaultVariants: {
			variant: "default",
			radius: "default",
		},
	},
);

type TabsTriggerProps<T extends ValidComponent = "button"> = TabsPrimitive.TabsTriggerProps<T> & {
	class?: string | undefined;
} & VariantProps<typeof tabsTriggerVariants>;

const TabsTrigger = <T extends ValidComponent = "button">(props: PolymorphicProps<T, TabsTriggerProps<T>>) => {
	const [local, others] = splitProps(props as TabsTriggerProps, ["class", "variant", "radius"]);
	return (
		<TabsPrimitive.Trigger
			class={cn(tabsTriggerVariants({ variant: local.variant, radius: local.radius }), local.class)}
			{...others}
		/>
	);
};

type TabsContentProps<T extends ValidComponent = "div"> = TabsPrimitive.TabsContentProps<T> & {
	class?: string | undefined;
};

const TabsContent = <T extends ValidComponent = "div">(props: PolymorphicProps<T, TabsContentProps<T>>) => {
	const [local, others] = splitProps(props as TabsContentProps, ["class"]);
	return (
		<TabsPrimitive.Content
			class={cn(
				"mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				local.class,
			)}
			{...others}
		/>
	);
};

type TabsIndicatorProps<T extends ValidComponent = "div"> = TabsPrimitive.TabsIndicatorProps<T> & {
	class?: string | undefined;
};

const TabsIndicator = <T extends ValidComponent = "div">(props: PolymorphicProps<T, TabsIndicatorProps<T>>) => {
	const [local, others] = splitProps(props as TabsIndicatorProps, ["class"]);
	return (
		<TabsPrimitive.Indicator
			class={cn(
				"duration-250ms absolute transition-all data-[orientation=horizontal]:-bottom-px data-[orientation=vertical]:-right-px data-[orientation=horizontal]:h-[2px] data-[orientation=vertical]:w-[2px]",
				local.class,
			)}
			{...others}
		/>
	);
};

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsIndicator };
